import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { X, Camera, RefreshCw } from "lucide-react";
import { beep } from "./posShared";

// Full-screen camera barcode scanner for mobile. Calls onDetect(code) once and closes.
export default function CameraScanner({ onDetect, onClose }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const readerRef = useRef(new BrowserMultiFormatReader());
  const doneRef = useRef(false);
  const [error, setError] = useState("");
  const [devices, setDevices] = useState([]);
  const [deviceIdx, setDeviceIdx] = useState(0);

  const stop = () => {
    try { controlsRef.current?.stop(); } catch (e) { console.debug("scanner stop error", e); }
    controlsRef.current = null;
    try {
      const stream = videoRef.current?.srcObject;
      stream?.getVideoTracks?.().forEach((t) => t.stop());
    } catch (e) { console.debug("scanner track stop error", e); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cams = await BrowserMultiFormatReader.listVideoInputDevices();
        if (mounted) setDevices(cams);
      } catch (e) { console.warn("No se pudieron listar las cámaras:", e); }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    doneRef.current = false;
    const start = async () => {
      if (!videoRef.current) return;
      const deviceId = devices[deviceIdx]?.deviceId;
      try {
        const controls = await readerRef.current.decodeFromVideoDevice(
          deviceId,
          videoRef.current,
          (result) => {
            if (result && !doneRef.current) {
              doneRef.current = true;
              const code = result.getText();
              beep(true);
              stop();
              onDetect(code);
              onClose();
            }
          }
        );
        if (mounted) controlsRef.current = controls;
        else controls.stop();
      } catch (e) {
        if (mounted) setError("No se pudo acceder a la cámara. Revisa los permisos del navegador.");
      }
    };
    start();
    return () => { mounted = false; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIdx, devices.length]);

  const close = () => { stop(); onClose(); };

  return (
    <div className="fixed inset-0 z-[120] bg-black flex flex-col" data-testid="camera-scanner">
      <div className="flex items-center justify-between px-4 py-3 text-cream">
        <span className="flex items-center gap-2 text-sm"><Camera size={16} /> Escanear con la cámara</span>
        <div className="flex items-center gap-2">
          {devices.length > 1 && (
            <button data-testid="camera-switch" onClick={() => setDeviceIdx((i) => (i + 1) % devices.length)} className="p-2 rounded-full hover:bg-white/10"><RefreshCw size={18} /></button>
          )}
          <button data-testid="camera-close" onClick={close} className="p-2 rounded-full hover:bg-white/10"><X size={20} /></button>
        </div>
      </div>
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="w-4/5 max-w-sm h-32 border-2 border-rose/90 rounded-xl shadow-[0_0_0_2000px_rgba(0,0,0,0.35)]" />
        </div>
        {error && (
          <div className="absolute bottom-8 left-4 right-4 bg-white text-espresso text-sm rounded-xl p-4 text-center">{error}</div>
        )}
      </div>
      <p className="text-center text-cream/70 text-xs py-3">Apunta al código de barras dentro del recuadro.</p>
    </div>
  );
}
